import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Shield, FileText, ScrollText, Clock, ChevronRight, Mail, HelpCircle, Lock, CheckCircle, type LucideIcon } from "lucide-react";
import sentinelforgeLogo from "@/assets/sentinelforge-logo.webp";
import { motion } from "framer-motion";
import WaitlistForm from "@/components/waitlist/WaitlistForm";
import BreadcrumbJsonLd from "@/components/seo/BreadcrumbJsonLd";
import FaqJsonLd from "@/components/seo/FaqJsonLd";
import PageMeta from "@/components/seo/PageMeta";
import ServiceJsonLd from "@/components/seo/ServiceJsonLd";
import { getIndustrySeo, OFFER_URL, PRICE_FROM_USD } from "@/lib/industry-seo";
import { INDUSTRIES } from "@/lib/industries";

interface FaqItem {
  q: string;
  a: string;
}

interface ValueProp {
  title: string;
  description: string;
}

interface IndustryPlaceholderPageProps {
  name: string;
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  headline: string;
  tagline: string;
  description: string;
  benefits: string[];
  valueProps: ValueProp[];
  features: { icon: LucideIcon; label: string }[];
  proposalTemplates: string[];
  contractTemplates: string[];
  faq: FaqItem[];
  ctaHeadline: string;
  ctaDescription: string;
  fullySupported?: boolean;
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" as const },
  }),
};

const IndustryPlaceholderPage = ({
  name, icon: Icon, iconColor, iconBg, headline, tagline, description, benefits, valueProps, features, proposalTemplates, contractTemplates, faq, ctaHeadline, ctaDescription, fullySupported,
}: IndustryPlaceholderPageProps) => {
  const BASE = "https://www.sentinelforgesystems.com";
  const slug = name.toLowerCase().replace(/\s*&\s*/g, "-").replace(/\s+/g, "-");
  const pageUrl = BASE + "/industries/" + slug;
  const seo = getIndustrySeo(slug);
  // Derive live status from the canonical industry registry; allow explicit prop override.
  const matchedIndustry = INDUSTRIES.find(
    (i) => i.name.toLowerCase() === name.toLowerCase()
  );
  const isLive = fullySupported ?? matchedIndustry?.status === "live";
  const isInDevelopment = matchedIndustry?.status === "development";
  const breadcrumbs = [
    { name: "Home", url: BASE + "/" },
    { name: "Industries", url: BASE + "/industries" },
    { name: name, url: pageUrl },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <BreadcrumbJsonLd items={breadcrumbs} />
      <FaqJsonLd items={faq} />
      <ServiceJsonLd
        name={name}
        serviceType={seo.serviceType}
        description={seo.serviceDescription}
        url={pageUrl}
        image={seo.ogImage}
        priceFrom={PRICE_FROM_USD}
        offerUrl={OFFER_URL}
        breadcrumbs={breadcrumbs}
      />
      <PageMeta
        title={`${name} Proposal Software — SentinelForge`}
        description={`${tagline} Create professional ${name.toLowerCase()} proposals, contracts, and estimates in minutes with SentinelForge.`}
        url={pageUrl}
        image={seo.ogImage}
      />
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={sentinelforgeLogo} alt="Sentinel Forge" className="h-7 w-7 object-contain" />
            <span className="text-lg font-bold text-foreground tracking-tight">Sentinel Forge</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/industries" className="hover:text-foreground transition-colors">Industries</Link>
            <a href="#features" className="hover:text-foreground transition-colors">Features</a>
            <a href="#faq" className="hover:text-foreground transition-colors">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/auth">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">Sign In</Button>
            </Link>
            <Link to={`/auth?signup&industry=${encodeURIComponent(name.toLowerCase().replace(/\s+/g, '-'))}`}>
              <Button size="sm" className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* BREADCRUMB */}
      <div className="pt-20 max-w-7xl mx-auto px-6">
        <Breadcrumb className="mb-0">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild><Link to="/industries">Industries</Link></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* HERO */}
      <section className="relative pt-12 pb-20 md:pt-16 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 gradient-dark" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-primary/5 blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border ${isLive ? "border-success/30 bg-success/10 text-success" : isInDevelopment ? "border-primary/30 bg-primary/10 text-primary" : "border-warning/30 bg-warning/10 text-warning"} text-sm font-medium mb-8`}>
              {isLive ? <CheckCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
              {isLive ? "Fully Supported" : isInDevelopment ? "In Development" : "Early Access — Coming Soon"}
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-foreground leading-[1.1] mb-6">
              {headline}{" "}
              <span className="text-gradient">{tagline}</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
              {description}
            </p>

            {!isLive && (
              <div className="mt-8">
                <WaitlistForm industry={name.toLowerCase().replace(/\s+/g, '-')} />
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            {isLive && (
              <Link to={`/auth?signup&industry=${encodeURIComponent(name.toLowerCase().replace(/\s+/g, '-'))}`}>
                <Button size="lg" className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90 px-8 text-base h-12">
                  Get Started <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            )}
              <Link to="/industries">
                <Button size="lg" variant="outline" className="border-border text-foreground hover:bg-secondary px-8 text-base h-12">
                  View All Industries
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VALUE PROPOSITIONS */}
      <section className="py-20 md:py-28 border-t border-border/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {valueProps.map((item, i) => (
              <motion.div key={item.title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeUp}>
                <Card className="border-border bg-card shadow-card h-full hover:border-primary/30 transition-colors duration-300">
                  <CardContent className="p-8">
                    <div className={`h-12 w-12 rounded-xl ${iconBg} flex items-center justify-center mb-5`}>
                      <Icon className={`h-6 w-6 ${iconColor}`} />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-20 md:py-28 border-t border-border/50 bg-secondary/20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything You Need to <span className="text-gradient">Grow Your Business</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              From automated proposals to digital contracts, Sentinel Forge handles the entire {name.toLowerCase()} workflow.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feat, i) => (
              <motion.div key={feat.label} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30px" }} variants={fadeUp}>
                <div className="flex items-center gap-4 p-5 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors duration-300">
                  <div className={`h-10 w-10 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
                    <feat.icon className={`h-5 w-5 ${iconColor}`} />
                  </div>
                  <span className="text-sm font-medium text-foreground">{feat.label}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TEMPLATES PREVIEW */}
      <section className="py-20 md:py-28 border-t border-border/50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Templates Built for <span className="text-gradient">{name}</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Industry-specific proposal and contract templates designed to save you time and impress clients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" /> Proposal Templates
              </h3>
              <Card className="border-border bg-card">
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    {proposalTemplates.map((t) => (
                      <li key={t} className="flex items-center gap-3 text-sm text-foreground">
                        <div className={`h-2 w-2 rounded-full ${iconBg}`} />
                        {t}
                      </li>
                    ))}
                  </ul>
                  {!isLive && (
                    <p className="text-xs text-muted-foreground mt-5 italic border-t border-border pt-4">
                      Templates are being finalized and will be available at launch.
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={2}>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <ScrollText className="h-5 w-5 text-primary" /> Contract Templates
              </h3>
              <Card className="border-border bg-card">
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    {contractTemplates.map((t) => (
                      <li key={t} className="flex items-center gap-3 text-sm text-foreground">
                        <div className={`h-2 w-2 rounded-full ${iconBg}`} />
                        {t}
                      </li>
                    ))}
                  </ul>
                  {!isLive && (
                    <p className="text-xs text-muted-foreground mt-5 italic border-t border-border pt-4">
                      Templates are being finalized and will be available at launch.
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-28 border-t border-border/50 bg-secondary/20">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h2>
            <p className="text-muted-foreground text-lg">Everything you need to know about {name.toLowerCase()} on Sentinel Forge.</p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-30px" }} variants={fadeUp} custom={1}>
            <Accordion type="single" collapsible className="space-y-3">
              {faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border bg-card rounded-xl px-6 data-[state=open]:border-primary/30 transition-colors duration-300">
                  <AccordionTrigger className="text-foreground font-medium text-left hover:no-underline py-5">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-border/50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">{ctaHeadline}</h2>
          <p className="text-muted-foreground text-lg mb-8">{ctaDescription}</p>
          {isLive ? (
            <>
              <Link to={`/auth?signup&industry=${encodeURIComponent(name.toLowerCase().replace(/\s+/g, '-'))}`}>
                <Button size="lg" className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90 px-10 text-base h-12">
                  Get Started Now <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <p className="text-sm text-muted-foreground mt-4">
                This industry is fully supported by Sentinel Forge. Additional industries will be added soon.
              </p>
            </>
          ) : (
            <div className="mt-2">
              <WaitlistForm industry={name.toLowerCase().replace(/\s+/g, '-')} />
            </div>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border/50 bg-card/50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img src={sentinelforgeLogo} alt="Sentinel Forge" className="h-5 w-5 object-contain" />
              <span className="text-sm font-semibold text-foreground">Sentinel Forge Systems</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
              <a href="mailto:support@sentinelforge.com" className="hover:text-foreground transition-colors flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" /> Contact
              </a>
              <a href="mailto:support@sentinelforge.com" className="hover:text-foreground transition-colors flex items-center gap-1">
                <HelpCircle className="h-3.5 w-3.5" /> Support
              </a>
              <Link to="/terms" className="hover:text-foreground transition-colors flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" /> Terms of Service
              </Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors flex items-center gap-1">
                <Lock className="h-3.5 w-3.5" /> Privacy Policy
              </Link>
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-xs text-muted-foreground">SentinelForge is expanding. More industries will be added soon.</p>
            <p className="text-xs text-muted-foreground mt-1">© {new Date().getFullYear()} Sentinel Forge Systems. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default IndustryPlaceholderPage;
